import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import {
  Modal,
  KeyboardAvoidingView,
  TouchableOpacity,
  View,
  Animated,
  PanResponder,
  useWindowDimensions,
  Platform,
  Dimensions,
  Pressable,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { X } from '@app/sharedComponents/Icons';
import styles from './styles';
import gs from '../../styles/globalStyles';
import { useAppSelector } from '@app/store/hooks';
import { useDarkMode } from '@app/utils';
import { ThemedText, ThemedTouchableOpacity } from '@app/theme/Components';
import useIsMobileBrowser from '@app/hooks/useIsMobileBrowser';
import { Theme, themeColorSet } from '@app/theme';
import { useTheme } from '@shopify/restyle';


const SUPPORTED_ORIENTATIONS = [
  'portrait',
  'portrait-upside-down',
  'landscape',
  'landscape-left',
  'landscape-right',
];

const windowHeight = Dimensions.get('window').height;
const windowWidth = Dimensions.get('window').width;

const Wrapper = ({ children }) => (
  <View style={[gs.flexOne, gs.justifyCenter, gs.alignItemsCenter]}>
    {children}
  </View>
);

const BottomSheetContainer = ({
  children,
  isVisible,
  close,
  onClose,
  dragComponent,
  closeOnBackdropPress = true,
  containerStyle,
  containsTextInput = false,
  borderTopRightRadius,
  borderTopLeftRadius,
  showCloseIcon = true,
  fullScreenHeight = false,
  isBottomSheetLtvType = false
}) => {
  const [isVisibleInternal, setIsVisibleInternal] = useState(false);
  const { height } = useWindowDimensions();
  const pan = useRef(new Animated.Value(height)).current;
  const animatedContentHeight = useRef(new Animated.Value(0)).current;
  const { top, bottom } = useSafeAreaInsets();
  const { showVoltDefaultHeader, platform , isFromAndroidSDK, isFromIOS } = useAppSelector((state) => state.platformParams);
  const isOpeninMobile = Platform.OS === 'web' ? window.innerWidth <= 768 : false;
  const isDarkMode = useDarkMode();
  const backgroundColor = isBottomSheetLtvType ? '#1D293999' : '#00000077';

  const isMobileBrowser = useIsMobileBrowser();
  const {colors} = useTheme<Theme>()

  
  const viewHeightSmallToMedium = () => {
    if (isOpeninMobile) return windowHeight;
    switch (true) {
      case platform === 'SDK_INVESTWELL':
        return windowHeight - 28;
      case showVoltDefaultHeader:
        return windowHeight - 60;
      default:
        return windowHeight;
    }
  };

  const viewHeightMediumToLarge = () => {
    switch (true) {
      case showVoltDefaultHeader:
        return windowHeight - 114;
      default:
        return windowHeight;
    }
  };

  const bottomSheetMaxHeight = viewHeightMediumToLarge()

  const createPanResponder = () =>
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onPanResponderMove: (e, gestureState) => {
        if (gestureState.dy > 0) {
          Animated.event([null, { dy: pan }], { useNativeDriver: true })(e, gestureState);
        }
      },
      onPanResponderRelease: (e, gestureState) => {
        if (gestureState.dy > 80) {
          setModalVisible(false, true);
        } else {
          Animated.spring(pan, {
            toValue: 0,
            useNativeDriver: true,
          }).start();
        }
      },
    });

  const panResponder = useMemo(() => createPanResponder(), []);

  useEffect(() => {
    if (isVisibleInternal === isVisible) return;
    setModalVisible(isVisible);
  }, [isVisible]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      document.body.style.overflow = isVisible ? 'hidden' : 'auto';
    }
    return () => {
      if (Platform.OS === 'web') document.body.style.overflow = 'auto';
    };
  }, [isVisible]);

  const setModalVisible = useCallback((visible, fromDrag = false) => {
    if (visible) {
      setIsVisibleInternal(true);
      Animated.timing(pan, {
        useNativeDriver: true,
        toValue: 0,
        duration: containsTextInput
          ? Platform.select({ ios: 0, android: 250, web: 500 })
          : Platform.OS === 'web'
          ? 300
          : 300,
      }).start();
    } else {
      Animated.timing(pan, {
        useNativeDriver: true,
        toValue: height,
        duration:
          containsTextInput && Platform.OS === 'android'
            ? 250
            : Platform.OS === 'web'
            ? (isBottomSheetLtvType ? 350 : 550)
            : 300,
      }).start(() => {
        setIsVisibleInternal(false);
        onClose && onClose();
        fromDrag && close();
      });
    }
  }, [height, containsTextInput, onClose, close, isBottomSheetLtvType]);

  const onContentLayout = (event) => {
    const { height } = event.nativeEvent.layout;
    const clampedHeight = fullScreenHeight ? bottomSheetMaxHeight : Math.min(height, bottomSheetMaxHeight);
    Animated.timing(animatedContentHeight, {
      toValue: clampedHeight,
      duration: 300,
      useNativeDriver: false,
    }).start();
  };

  return (
    <Modal
      transparent
      animationType={Platform.OS === 'web' ? 'none' : 'fade'}
      visible={isVisibleInternal}
      supportedOrientations={SUPPORTED_ORIENTATIONS}
      onRequestClose={close}
    >
      <Wrapper containsTextInput={containsTextInput}>
        <KeyboardAvoidingView
          enabled={true}
          behavior={Platform.select({ ios: 'padding', android: undefined, web: 'padding' })}
          style={[
            styles.wrapper,
            gs.setPaddingTop(top),
            {
              backgroundColor,
              // height : undefined,
              height: Platform.OS === 'web' ? isFromAndroidSDK || isFromIOS || isMobileBrowser ? undefined : Platform.OS === 'web' ? bottomSheetMaxHeight : windowHeight : Dimensions.get('screen').height,
              marginBottom:
                bottomSheetMaxHeight === windowHeight
                  ? 0
                  : Platform.select({ web: windowWidth > 767 ? 30 : 0, ios: 0, android: 0 }),
            },
            isOpeninMobile && { top: 0 },
          ]}
        >
          <TouchableOpacity
            style={styles.mask}
            activeOpacity={1}
            onPress={closeOnBackdropPress ? close : () => {}}
          />
          <Animated.View
          renderToHardwareTextureAndroid={true}
          shouldRasterizeIOS={true}
            style={[
              styles.container,
              gs.setPaddingBottom(bottom),
              containerStyle,
              {
                backgroundColor: themeColorSet['bg-fill-primary'],
                transform: [{ translateY: pan }],
                height: Platform.OS === 'web' ? animatedContentHeight : undefined,
                borderTopLeftRadius: fullScreenHeight ? 0 : borderTopLeftRadius,
                borderTopRightRadius: fullScreenHeight ? 0 : borderTopRightRadius,
              },
            ]}
          >
            <View
              onLayout={onContentLayout}
              style={{ marginBottom: isOpeninMobile && containsTextInput ? 40 : 0 }}
            >
            { showCloseIcon &&
            <ThemedTouchableOpacity onPress={close} style={styles.closeIcon} hitSlop={20}>
                <X size={20} style={{color:colors['icon-primary']}} />
              </ThemedTouchableOpacity>}
              {children}
            </View>
          </Animated.View>
        </KeyboardAvoidingView>
      </Wrapper>
    </Modal>
  );
};

export default BottomSheetContainer;
